/**
 * TeamCardsView — Planning Board "Team" mode.
 *
 * Renders one card per active workspace member and slots their assigned
 * tasks inside. Cards mirror the visual language of Planning Board's
 * Project cards (dot indicator, header row, task filter chip, task list
 * with priority dot + status + due) plus the 4 status-update shortcuts.
 *
 * Group-by supports:
 *   • None       — flat responsive grid
 *   • Role       — group by workspace role
 *   • Status     — group by "has overdue" / "has blocked" / "on track" / "no tasks"
 *
 * All filters coming from PlanningBoard are already applied to `tasks`,
 * so this view just re-buckets by `assignee_id`.
 */
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, User as UserIcon, ExternalLink, Rows } from "lucide-react";
import TaskShortcutsRow from "@/components/tasks/TaskShortcutsRow";

const statusChip = (s) => {
  const map = {
    "backlog":    "bg-muted text-muted-foreground border-border",
    "todo":       "bg-blue-500/10 text-blue-700 border-blue-500/30",
    "in_progress":"bg-amber-500/10 text-amber-800 border-amber-500/30",
    "in progress":"bg-amber-500/10 text-amber-800 border-amber-500/30",
    "review":     "bg-purple-500/10 text-purple-700 border-purple-500/30",
    "blocked":    "bg-rose-500/10 text-rose-700 border-rose-500/30",
    "done":       "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  };
  return map[(s || "").toLowerCase()] || "bg-muted text-muted-foreground border-border";
};

const priorityDot = (p) => {
  const c = (p || "").toLowerCase();
  if (c === "urgent" || c === "high") return "bg-rose-500";
  if (c === "medium") return "bg-amber-500";
  if (c === "low") return "bg-emerald-500";
  return "bg-muted-foreground/40";
};

const fmtDueShort = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const now = new Date();
  const diffDays = Math.round((dt - now) / 86400000);
  const abs = dt.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
  if (diffDays < 0) return `${abs} · ${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return `${abs} · today`;
  if (diffDays <= 7) return `${abs} · in ${diffDays}d`;
  return abs;
};

const GROUP_OPTIONS = [
  { v: "none", l: "No grouping" },
  { v: "role", l: "Group by role" },
  { v: "health", l: "Group by workload" },
  { v: "project", l: "By project (one card per member × project)" },
  { v: "brand", l: "By brand (one card per member × brand)" },
  { v: "client", l: "By client (one card per member × client)" },
];

export default function TeamCardsView({
  users = [],
  members = [],
  tasks = [],
  projects = [],
  brands = [],
  clients = [],
  hideEmpty = false,
  workspaceName = "",
  currentUserId = null,
  statusTemplates = null,
  senderName = "",
  onOpenTask,
}) {
  const [collapsed, setCollapsed] = useState(new Set());
  const [taskFilter, setTaskFilter] = useState("all_open"); // all | all_open | overdue | blocked | done
  const [groupBy, setGroupBy] = useState("none");

  const projById = useMemo(() => {
    const m = new Map();
    for (const p of projects || []) m.set(p.id, p);
    return m;
  }, [projects]);
  const brandById = useMemo(() => {
    const m = new Map();
    for (const b of brands || []) m.set(b.id, b);
    return m;
  }, [brands]);
  const clientById = useMemo(() => {
    const m = new Map();
    for (const c of clients || []) m.set(c.id, c);
    return m;
  }, [clients]);

  // Build tasksByUid, sorted by overdue → priority → due asc
  const tasksByUid = useMemo(() => {
    const m = new Map();
    for (const t of tasks || []) {
      const ids = Array.isArray(t.assignee_ids) && t.assignee_ids.length
        ? t.assignee_ids : (t.assignee_id ? [t.assignee_id] : []);
      for (const uid of ids) {
        if (!m.has(uid)) m.set(uid, []);
        m.get(uid).push(t);
      }
    }
    const prioRank = { urgent: 0, high: 1, medium: 2, low: 3, "": 4 };
    for (const arr of m.values()) {
      arr.sort((a, b) => {
        const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        const ao = ad < Date.now(), bo = bd < Date.now();
        if (ao !== bo) return ao ? -1 : 1;
        const ap = prioRank[(a.priority || "").toLowerCase()] ?? 4;
        const bp = prioRank[(b.priority || "").toLowerCase()] ?? 4;
        if (ap !== bp) return ap - bp;
        return ad - bd;
      });
    }
    return m;
  }, [tasks]);

  // Card-level "task filter" chip (mimics ProjectCard's chip).
  const filterFn = useMemo(() => (t) => {
    const s = (t.status || "").toLowerCase();
    if (taskFilter === "all") return true;
    if (taskFilter === "all_open") return s !== "done";
    if (taskFilter === "overdue") return t.due_date && new Date(t.due_date) < new Date() && s !== "done";
    if (taskFilter === "blocked") return s === "blocked";
    if (taskFilter === "done") return s === "done";
    return true;
  }, [taskFilter]);

  // Assemble the visible user list.
  const displayUsers = useMemo(() => {
    const seen = new Set();
    const arr = [];
    for (const mem of members || []) {
      if (mem.status && mem.status !== "active") continue;
      const uid = mem.user_id || mem.id;
      if (!uid || seen.has(uid)) continue;
      seen.add(uid);
      const u = (users || []).find((x) => x.id === uid) || {};
      arr.push({
        id: uid,
        name: u.name || mem.name || mem.email || "Unknown",
        email: u.email || mem.email,
        phone: mem.phone || u.phone || null,
        role: mem.role || "member",
        avatar_url: mem.avatar_url || u.avatar_url,
      });
    }
    for (const u of users || []) {
      if (!seen.has(u.id)) {
        seen.add(u.id);
        arr.push({ id: u.id, name: u.name || u.email || "Unknown",
                   email: u.email, phone: u.phone || null, role: "member" });
      }
    }
    return arr.sort((a, b) => a.name.localeCompare(b.name));
  }, [members, users]);

  const shown = useMemo(() => displayUsers.filter((u) => {
    const rows = (tasksByUid.get(u.id) || []).filter(filterFn);
    if (hideEmpty && rows.length === 0) return false;
    return true;
  }), [displayUsers, tasksByUid, hideEmpty, taskFilter, filterFn]);

  // Group buckets. For "role"/"health" — buckets group USERS, each user
  // gets ONE card with all their tasks. For "project"/"brand"/"client"
  // — buckets split each user's tasks into per-dimension cards (a user
  // with tasks in 3 projects gets 3 cards). Buckets themselves are the
  // dimension groups (e.g. "Project A", "Project B").
  const groups = useMemo(() => {
    if (groupBy === "none") return [{ key: "_", label: "", cards: shown.map((u) => ({ user: u, tasks: (tasksByUid.get(u.id) || []).filter(filterFn), suffix: "", cardId: u.id })) }];
    if (groupBy === "role") {
      const buckets = new Map();
      for (const u of shown) {
        const k = u.role || "member";
        if (!buckets.has(k)) buckets.set(k, []);
        buckets.get(k).push({ user: u, tasks: (tasksByUid.get(u.id) || []).filter(filterFn), suffix: "", cardId: u.id });
      }
      const order = ["owner", "admin", "member"];
      const known = order.filter((k) => buckets.has(k));
      const extra = [...buckets.keys()].filter((k) => !order.includes(k)).sort();
      return [...known, ...extra].map((k) => ({ key: k, label: k.replace("_", " "), cards: buckets.get(k) }));
    }
    if (groupBy === "health") {
      const overdue = [], blocked = [], onTrack = [], noTasks = [];
      for (const u of shown) {
        const arr = (tasksByUid.get(u.id) || []).filter(filterFn);
        const card = { user: u, tasks: arr, suffix: "", cardId: u.id };
        if (arr.length === 0) noTasks.push(card);
        else if (arr.some((t) => t.due_date && new Date(t.due_date) < new Date() && (t.status || "").toLowerCase() !== "done")) overdue.push(card);
        else if (arr.some((t) => (t.status || "").toLowerCase() === "blocked")) blocked.push(card);
        else onTrack.push(card);
      }
      return [
        { key: "overdue",  label: "Has overdue",   cards: overdue },
        { key: "blocked",  label: "Has blocked",   cards: blocked },
        { key: "on_track", label: "On track",      cards: onTrack },
        { key: "no_tasks", label: "No tasks",      cards: noTasks },
      ].filter((g) => g.cards.length > 0);
    }
    // ── Split per-user tasks across project/brand/client dimensions.
    // Bucket at the top level is the dimension VALUE (e.g. project name).
    // Cards within each bucket are (user × dimension_value) combos.
    if (groupBy === "project" || groupBy === "brand" || groupBy === "client") {
      const dimBuckets = new Map(); // dimKey → {label, cards: []}
      const dimValue = (t) => {
        const proj = projById.get(t.project_id);
        if (!proj) return { key: "_unknown", label: "— (no project)" };
        if (groupBy === "project") return { key: proj.id, label: proj.name };
        if (groupBy === "brand") {
          const bid = proj.brand_id || "_unassigned";
          const bname = proj.brand_name || brandById.get(proj.brand_id)?.name || (proj.brand_id ? `Brand ${String(proj.brand_id).slice(0,6)}` : "— (no brand)");
          return { key: bid, label: bname };
        }
        if (groupBy === "client") {
          const cid = proj.client_id || "_unassigned";
          const cname = proj.client_name || clientById.get(proj.client_id)?.name || (proj.client_id ? `Client ${String(proj.client_id).slice(0,6)}` : "— (no client)");
          return { key: cid, label: cname };
        }
        return { key: "_", label: "" };
      };
      for (const u of shown) {
        const arr = (tasksByUid.get(u.id) || []).filter(filterFn);
        // Group this user's tasks by dimension.
        const perDim = new Map(); // dimKey → tasks[]
        for (const t of arr) {
          const dv = dimValue(t);
          if (!perDim.has(dv.key)) perDim.set(dv.key, { label: dv.label, tasks: [] });
          perDim.get(dv.key).tasks.push(t);
        }
        if (perDim.size === 0 && !hideEmpty) {
          // Hide-empty toggle already dropped users with 0 tasks; when
          // it's OFF we still want to show the member somewhere. Land
          // them under an "— (no tasks)" bucket so the count is honest.
          const k = "_none";
          if (!dimBuckets.has(k)) dimBuckets.set(k, { label: "— (no tasks)", cards: [] });
          dimBuckets.get(k).cards.push({ user: u, tasks: [], suffix: "", cardId: `${u.id}::none` });
          continue;
        }
        for (const [k, v] of perDim.entries()) {
          if (!dimBuckets.has(k)) dimBuckets.set(k, { label: v.label, cards: [] });
          dimBuckets.get(k).cards.push({
            user: u,
            tasks: v.tasks,
            suffix: v.label,
            cardId: `${u.id}::${k}`,
          });
        }
      }
      // Sort buckets alphabetically by label but keep unknown/none last.
      const arr = [...dimBuckets.entries()].map(([key, v]) => ({ key, label: v.label, cards: v.cards }));
      arr.sort((a, b) => {
        const aBottom = a.key.startsWith("_");
        const bBottom = b.key.startsWith("_");
        if (aBottom !== bBottom) return aBottom ? 1 : -1;
        return (a.label || "").localeCompare(b.label || "");
      });
      return arr;
    }
    return [{ key: "_", label: "", cards: shown.map((u) => ({ user: u, tasks: (tasksByUid.get(u.id) || []).filter(filterFn), suffix: "", cardId: u.id })) }];
  }, [shown, groupBy, tasksByUid, taskFilter, filterFn, projById, brandById, clientById, hideEmpty]);

  return (
    <div className="p-3 sm:p-4 space-y-3" data-testid="planning-team-cards-view">
      {/* Toolbar: task filter + group-by */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Show</label>
        <select value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)}
                className="h-7 text-xs border border-border rounded-none px-1.5 bg-background"
                data-testid="team-cards-task-filter">
          <option value="all">All tasks</option>
          <option value="all_open">Open only</option>
          <option value="overdue">Overdue</option>
          <option value="blocked">Blocked</option>
          <option value="done">Done</option>
        </select>
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground ml-2 inline-flex items-center gap-1">
          <Rows className="w-3 h-3" /> Group
        </label>
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
                className="h-7 text-xs border border-border rounded-none px-1.5 bg-background"
                data-testid="team-cards-group-by">
          {GROUP_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
        <span className="ml-auto text-[11px] text-muted-foreground">
          {shown.length} member{shown.length === 1 ? "" : "s"} · {tasks.length} task{tasks.length === 1 ? "" : "s"} in view
        </span>
      </div>

      {shown.length === 0 && (
        <div className="text-center text-sm text-muted-foreground py-16 border border-dashed border-border">
          {hideEmpty ? "No team members have tasks matching the current filters." : "No active team members to display."}
        </div>
      )}

      {groups.map((g) => (
        <div key={g.key} data-testid={`team-cards-group-${g.key}`}>
          {g.label && (
            <div className="flex items-center gap-2 py-1.5 mb-2 border-b border-primary/30">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-primary">{g.label}</span>
              <span className="text-[9px] text-muted-foreground">{g.cards.length} card{g.cards.length === 1 ? "" : "s"}</span>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3">
            {g.cards.map((c) => (
              <TeamCard
                key={c.cardId}
                cardId={c.cardId}
                user={c.user}
                tasks={c.tasks}
                suffix={c.suffix}
                projById={projById}
                projects={projects}
                workspaceName={workspaceName}
                templates={statusTemplates}
                senderName={senderName}
                isMe={c.user.id === currentUserId}
                collapsed={collapsed.has(c.cardId)}
                onToggle={() => setCollapsed((prev) => {
                  const n = new Set(prev);
                  n.has(c.cardId) ? n.delete(c.cardId) : n.add(c.cardId);
                  return n;
                })}
                onOpenTask={onOpenTask}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamCard({ user, tasks, suffix, cardId, projById, workspaceName, templates, senderName, isMe, collapsed, onToggle, onOpenTask }) {
  const overdue = tasks.filter((t) => t.due_date && new Date(t.due_date) < new Date() && (t.status || "").toLowerCase() !== "done").length;
  const blocked = tasks.filter((t) => (t.status || "").toLowerCase() === "blocked").length;
  const containerId = `team-${cardId || user.id}`;

  return (
    <div
      data-shortcut-container={containerId}
      data-testid={`team-card-${cardId || user.id}`}
      className={`border ${isMe ? "border-primary/60 shadow-[0_0_0_1px_hsl(var(--primary)/0.3)]" : "border-border"} bg-card flex flex-col`}
    >
      {/* Header — ProjectCard-style: 2-line stack + tinted background */}
      <div className="px-3 py-2 border-b border-border bg-muted/30 flex items-center gap-2">
        <button
          onClick={onToggle}
          className="p-0.5 hover:bg-muted rounded"
          title={collapsed ? "Expand" : "Collapse"}
          data-testid={`team-card-toggle-${cardId || user.id}`}
        >
          {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-semibold shrink-0 overflow-hidden">
          {user.avatar_url
            ? <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            : (user.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase() || <UserIcon className="w-3.5 h-3.5" />
          }
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold truncate" data-testid={`team-card-name-${cardId || user.id}`}>
            {user.name}
            {isMe && <span className="ml-1 text-[9px] uppercase tracking-widest text-primary">you</span>}
            {user.role && user.role !== "member" && (
              <span className="ml-1 text-[9px] uppercase tracking-widest text-muted-foreground">· {user.role}</span>
            )}
          </div>
          {suffix && (
            <div className="text-[10.5px] text-primary truncate font-medium" title={suffix}>
              → {suffix}
            </div>
          )}
          <div className="text-[10.5px] text-muted-foreground truncate">
            {tasks.length} task{tasks.length === 1 ? "" : "s"}
            {overdue > 0 && <span className="text-rose-600 font-semibold ml-1">· {overdue} overdue</span>}
            {blocked > 0 && <span className="text-rose-600 font-semibold ml-1">· {blocked} blocked</span>}
          </div>
        </div>
      </div>

      {/* Shortcuts row (Email · WhatsApp · Copy text · Copy image) */}
      <div className="px-2 py-1 border-b border-border bg-background">
        <TaskShortcutsRow
          templates={templates}
          recipient={{ name: user.name, email: user.email, phone: user.phone }}
          senderName={senderName}
          tasks={tasks}
          workspaceName={workspaceName}
          projectsById={projById}
          containerId={containerId}
        />
      </div>

      {/* Body */}
      {!collapsed && (
        <ul className="flex-1 min-h-0 overflow-auto" data-testid={`team-card-tasks-${cardId || user.id}`}>
          {tasks.length === 0 && (
            <li className="p-4 text-center text-[11px] text-muted-foreground italic border-t border-border">
              No tasks in this view.
            </li>
          )}
          {tasks.map((t) => {
            const proj = projById.get(t.project_id);
            const isOverdue = t.due_date && new Date(t.due_date) < new Date() && (t.status || "").toLowerCase() !== "done";
            return (
              <li key={t.id}
                  onClick={() => onOpenTask?.(t.id)}
                  className="px-3 py-1.5 border-b border-border/50 last:border-b-0 hover:bg-muted/30 cursor-pointer group text-[12px]"
                  data-testid={`team-card-task-${cardId || user.id}-${t.id}`}>
                <div className="flex items-start gap-1.5">
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${priorityDot(t.priority)}`} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-medium group-hover:text-primary flex items-center gap-1">
                      <span className="truncate">{t.title}</span>
                      <ExternalLink className="w-2.5 h-2.5 opacity-0 group-hover:opacity-60 shrink-0" />
                    </div>
                    <div className="text-[10px] text-muted-foreground flex items-center gap-1.5 flex-wrap mt-0.5">
                      {t.status && (
                        <span className={`chip text-[9px] px-1 py-0 ${statusChip(t.status)}`}>{t.status}</span>
                      )}
                      {proj && <span className="truncate max-w-[140px]">· {proj.name}</span>}
                      {t.due_date && (
                        <span className={`font-mono ${isOverdue ? "text-rose-600 font-semibold" : ""}`}>
                          · {fmtDueShort(t.due_date)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
